/**
 * Helpers puros da aba "Horário de atendimento" do drawer da empresa.
 *
 * Duas gramáticas convivem e NÃO devem se misturar:
 *  - a PERSISTIDA (`BusinessWeekDay[]` de `@troia-v3/types`), já normalizada
 *    pelo backend: faixas em minutos, ordenadas, fundidas, e turno noturno
 *    PARTIDO em dois `weekday`;
 *  - a de FORMULÁRIO (`BusinessDayForm`), uma linha por dia da semana com
 *    início, fim e UM intervalo — a pausa do almoço, que é o que o dono pediu
 *    e que o formulário de turnos não tem.
 *
 * `toWeekForm` recompõe o turno noturno para a apresentação (a feiura fica
 * aqui, como manda a spec §4.4) e `fromWeekForm` devolve a forma de fio, que o
 * backend re-normaliza. NENHUM cálculo de SLA passa por este arquivo: o
 * frontend não possui o calendário, e contagem regressiva vem pronta do
 * servidor.
 *
 * A fusão de cauda só acontece quando é a INVERSA EXATA da partição que o
 * backend faz na escrita (`endMin <= startMin` vira `[startMin,1440]` no dia
 * + `[0,endMin]` no seguinte) — não basta "uma faixa de cada lado, sem
 * pausa, tocando em 1440/0": sábado `[1200,1440]` + domingo `[0,1440]`
 * (24h corridas) satisfaz isso e NÃO é o mesmo documento que sábado
 * `14:00→06:00`, que reparte para `[1200,1440]`+`[0,360]` — fundir os dois
 * faria o domingo inteiro desaparecer. A terceira condição, que fecha esse
 * buraco, é `tailEnd < prevStart` (estritamente menor): só nesse caso o
 * merge produz `{prevStart, tailEnd}` com `endMin < startMin`, que o backend
 * reparte de volta no MESMO par original. Ver o docstring de `toWeekForm`
 * para as três condições completas.
 */
import type { BusinessDaySlot, BusinessWeekDay } from '@troia-v3/types';

export interface BusinessDayForm {
  /** 0 = domingo. */
  weekday: number;
  enabled: boolean;
  /** 'HH:MM'. */
  start: string;
  /** 'HH:MM'; '24:00' representa o fim do dia. */
  end: string;
  hasBreak: boolean;
  breakStart: string;
  breakEnd: string;
  /** Dia gravado fora desta tela (3+ faixas) e simplificado na exibição. */
  truncated: boolean;
}

export const WEEKDAY_LABELS = [
  'Domingo',
  'Segunda-feira',
  'Terça-feira',
  'Quarta-feira',
  'Quinta-feira',
  'Sexta-feira',
  'Sábado',
] as const;

export function minutesToHHMM(minutes: number): string {
  const clamped = Math.max(0, Math.min(1440, Math.round(minutes)));
  return `${String(Math.floor(clamped / 60)).padStart(2, '0')}:${String(clamped % 60).padStart(2, '0')}`;
}

export function hhmmToMinutes(value: string): number {
  const [hours = '0', minutes = '0'] = value.split(':');
  return Number(hours) * 60 + Number(minutes);
}

/** Linha desligada, com os valores que o interruptor restaura ao ligar. */
export function emptyDayForm(weekday: number): BusinessDayForm {
  return {
    weekday,
    enabled: false,
    start: '08:00',
    end: '18:00',
    hasBreak: true,
    breakStart: '12:00',
    breakEnd: '13:00',
    truncated: false,
  };
}

/** `true` quando o turno atravessa a meia-noite (22:00 → 06:00). */
export function crossesMidnight(row: BusinessDayForm): boolean {
  return hhmmToMinutes(row.end) <= hhmmToMinutes(row.start);
}

/** Opções de 15 em 15 minutos. `includeEndOfDay` acrescenta 24:00. */
export function buildTimeOptions(includeEndOfDay: boolean): Array<{ value: string; label: string }> {
  const last = includeEndOfDay ? 1440 : 1425;
  const options: Array<{ value: string; label: string }> = [];
  for (let minute = includeEndOfDay ? 15 : 0; minute <= last; minute += 15) {
    const value = minutesToHHMM(minute);
    options.push({ value, label: minute === 1440 ? '24:00 (fim do dia)' : value });
  }
  return options;
}

/**
 * Documento → 7 linhas de formulário, sempre nesta ordem (domingo a sábado).
 * O passo delicado é desfazer a partição do turno noturno feita pelo backend:
 * uma faixa que começa em 0 num dia cujo ANTERIOR termina em 1440 é cauda, e
 * pertence à linha do dia anterior — mostrá-la como um expediente próprio é o
 * erro clássico, e faz o resumo semanal contar certo mas a tela mentir.
 *
 * A fusão só é segura quando é a INVERSA EXATA da partição que o backend faz
 * na escrita (`endMin <= startMin` vira `[startMin,1440]` + `[0,endMin]` no
 * dia seguinte). As TRÊS condições precisam valer ao mesmo tempo:
 *  1. o dia anterior (`prev`) tem EXATAMENTE uma faixa, SEM pausa, e ela
 *     TERMINA em 1440 (`prev.endMin === 1440`);
 *  2. o dia seguinte (`next`) tem EXATAMENTE uma faixa, SEM pausa, e ela
 *     COMEÇA em 0 (`next.startMin === 0`);
 *  3. `next.endMin < prev.startMin` (tailEnd ESTRITAMENTE MENOR que
 *     prevStart) — só assim o merge produz `{prevStart, tailEnd}` com
 *     `endMin < startMin`, que o backend reparte de volta no MESMO par.
 *     Sem essa terceira condição: `tailEnd > prevStart` faz o merge virar
 *     dia normal (o backend nunca reparte de volta — o domingo desaparece,
 *     ex.: sábado `[1200,1440]` + domingo 24h `[0,1440]`, ou sábado 24h
 *     `[0,1440]` + domingo `[0,360]`, que colapsaria as 24h de sábado para
 *     6h); `tailEnd === prevStart` faz o merge ter DURAÇÃO ZERO, que o
 *     backend rejeita com erro (a tela produziria um 400 salvando algo que
 *     ela própria montou).
 * Fora dessas três condições, NÃO funde: cada dia é mostrado como está
 * persistido — inclusive quando não há pausa em nenhum dos dois lados.
 */
export function toWeekForm(week: BusinessWeekDay[] | undefined): BusinessDayForm[] {
  const rows = Array.from({ length: 7 }, (_unused, weekday) => emptyDayForm(weekday));
  if (!week || week.length === 0) return rows;

  const slotsByWeekday = new Map<number, BusinessDaySlot[]>();
  for (const day of week) {
    slotsByWeekday.set(day.weekday, [...day.slots].sort((a, b) => a.startMin - b.startMin));
  }

  // Consome as caudas. A varredura é independente de ordem: só a PRIMEIRA
  // faixa é removida, e a checagem olha a ÚLTIMA faixa do dia anterior.
  // As TRÊS condições do docstring acima têm que valer juntas — a 3ª
  // (tailEnd < prevStart) é o que garante que o merge é a INVERSA EXATA da
  // partição do backend, não só "parece" turno noturno.
  const tailEnd = new Map<number, number>();
  for (const [weekday, slots] of slotsByWeekday) {
    const first = slots[0];
    if (slots.length !== 1 || !first || first.startMin !== 0) continue;

    const previousWeekday = (weekday + 6) % 7;
    const previous = slotsByWeekday.get(previousWeekday);
    const previousOnly = previous && previous.length === 1 ? previous[0] : undefined;

    if (previousOnly && previousOnly.endMin === 1440 && first.endMin < previousOnly.startMin) {
      tailEnd.set(previousWeekday, first.endMin);
      slotsByWeekday.set(weekday, []);
    }
  }

  for (const [weekday, slots] of slotsByWeekday) {
    const row = rows[weekday];
    const first = slots[0];
    const last = slots[slots.length - 1];
    if (!row || !first || !last) continue;

    const tail = tailEnd.get(weekday);
    row.enabled = true;
    row.start = minutesToHHMM(first.startMin);
    row.end = minutesToHHMM(tail ?? last.endMin);
    row.hasBreak = slots.length > 1;
    row.breakStart = slots.length > 1 ? minutesToHHMM(first.endMin) : '12:00';
    row.breakEnd = slots.length > 1 ? minutesToHHMM(last.startMin) : '13:00';
    row.truncated = slots.length > 2;
  }

  return rows;
}

/**
 * Formulário → forma de fio. Turno noturno vai CRU (`endMin < startMin`): quem
 * parte em dois `weekday` é `normalizeBusinessWeek` no backend, e duplicar
 * essa regra aqui seria o segundo motor que a spec proíbe.
 */
export function fromWeekForm(rows: BusinessDayForm[]): BusinessWeekDay[] {
  const week: BusinessWeekDay[] = [];

  for (const row of rows) {
    if (!row.enabled) continue;

    const startMin = hhmmToMinutes(row.start);
    const endMin = hhmmToMinutes(row.end);

    // Pausa não existe em turno noturno — a UI desabilita os controles.
    if (endMin <= startMin || !row.hasBreak) {
      week.push({ weekday: row.weekday, slots: [{ startMin, endMin }] });
      continue;
    }

    week.push({
      weekday: row.weekday,
      slots: [
        { startMin, endMin: hhmmToMinutes(row.breakStart) },
        { startMin: hhmmToMinutes(row.breakEnd), endMin },
      ],
    });
  }

  return week;
}

export function dayBusinessMinutes(row: BusinessDayForm): number {
  if (!row.enabled) return 0;

  const startMin = hhmmToMinutes(row.start);
  const endMin = hhmmToMinutes(row.end);
  const span = endMin > startMin ? endMin - startMin : endMin + 1440 - startMin;

  if (endMin <= startMin || !row.hasBreak) return span;

  return span - Math.max(0, hhmmToMinutes(row.breakEnd) - hhmmToMinutes(row.breakStart));
}

export function weeklyBusinessMinutes(rows: BusinessDayForm[]): number {
  return rows.reduce((total, row) => total + dayBusinessMinutes(row), 0);
}

/** Resumo do rodapé da aba: "45 horas úteis por semana". */
export function formatWeeklySummary(rows: BusinessDayForm[]): string {
  const minutes = weeklyBusinessMinutes(rows);
  if (minutes === 0) return 'Nenhuma hora útil por semana';

  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  const amount = rest === 0 ? String(hours) : `${hours}h${String(rest).padStart(2, '0')}`;
  return `${amount} ${hours === 1 && rest === 0 ? 'hora útil' : 'horas úteis'} por semana`;
}

/**
 * Espelha o que o Zod do backend recusa, para o usuário ver a mensagem em
 * português antes de tomar 422. Devolve `null` quando está tudo certo.
 */
export function validateWeekForm(rows: BusinessDayForm[]): string | null {
  const active = rows.filter((row) => row.enabled);
  if (active.length === 0) {
    return 'Ative pelo menos um dia da semana — ou desligue o horário de atendimento.';
  }

  for (const row of active) {
    const label = WEEKDAY_LABELS[row.weekday] ?? 'Dia';
    const startMin = hhmmToMinutes(row.start);
    const endMin = hhmmToMinutes(row.end);

    if (startMin === endMin) {
      return `${label}: o fim do expediente não pode ser igual ao início.`;
    }
    if (endMin < startMin || !row.hasBreak) continue;

    const breakStart = hhmmToMinutes(row.breakStart);
    const breakEnd = hhmmToMinutes(row.breakEnd);

    if (breakEnd <= breakStart) {
      return `${label}: o fim do intervalo precisa ser maior que o início.`;
    }
    if (breakStart <= startMin || breakEnd >= endMin) {
      return `${label}: o intervalo precisa ficar dentro do expediente.`;
    }
  }

  return null;
}
